import { useCallback, useMemo, useState } from "react"
import { Modal } from "@nasti/ui/modal"
import Map, { Marker } from "react-map-gl"
import mapboxgl from "mapbox-gl"
import { parseWkbPoint } from "@nasti/common/utils"
import { SpeciesListItem } from "@/components/species/SpeciesListItem"
import { PencilIcon, ShoppingBag, TrashIcon, XIcon } from "lucide-react"
import { usePeople } from "@/hooks/usePeople"
import { Button } from "@nasti/ui/button"
import { Badge } from "@nasti/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@nasti/ui/tabs"

import useUserStore from "@/store/userStore"
import { UpdateCollectionWizardModal } from "./CollectionFormModal"
import { useOpenClose, useToast } from "@nasti/ui/hooks"
import {
  useCollectionPhotos,
  CollectionPhotoSignedUrl,
} from "@/hooks/useCollectionPhotos"
import { CollectionPhotoCard } from "../collectionPhotos/CollectionPhotoCard"
import { useDeleteCollection } from "@/hooks/useUpdateCollection"
import { Link, useLocation } from "@tanstack/react-router"
import { useTripDetail } from "@/hooks/useTripDetail"
import { Dialog, DialogContent, DialogTitle } from "@nasti/ui/dialog"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@nasti/ui/carousel"
import { AudioTab } from "@/components/common/AudioTab"
import { useCollectionAudio } from "@/hooks/useEntityAudio"
import { PhenologyRangeDisplay } from "@nasti/ui/phenologyRangeDisplay"
import { usePersons } from "@/hooks/usePersons"
import { formatDuration } from "@/lib/duration"
import { useCollection } from "@/hooks/useCollection"
import { useCollectionContainers } from "@/hooks/useContainers"
import { MATERIAL_TYPE_LABELS, toMaterialTypes } from "@nasti/common/types"

const PhotosTab = ({
  photos,
  onClickPhoto,
}: {
  photos: CollectionPhotoSignedUrl[]
  onClickPhoto?: (photo: CollectionPhotoSignedUrl) => void
}) => {
  if (!photos || photos.length === 0)
    return (
      <p className="py-4 text-center text-gray-500">
        No photos added yet. Upload some photos to get started.
      </p>
    )
  return (
    <div className="space-y-4">
      <div className="grid max-h-96 grid-cols-2 gap-4 overflow-scroll lg:grid-cols-3">
        {photos.map((photo) => (
          <CollectionPhotoCard
            key={photo.id}
            photo={photo}
            onClickPhoto={onClickPhoto}
          />
        ))}
      </div>
    </div>
  )
}

const EditButtons = ({
  openUpdateModal,
  openDeleteModal,
}: {
  openUpdateModal: () => void
  openDeleteModal: () => void
}) => (
  <span className="inline-flex space-x-2">
    <Button
      size={"icon"}
      onClick={openUpdateModal}
      title="Edit Collection"
      variant={"ghost"}
    >
      <PencilIcon className="h-4 w-4" />
    </Button>
    <Button
      size={"icon"}
      onClick={openDeleteModal}
      title="Delete Collection"
      className="dark:text-primary-foreground cursor-pointer bg-transparent text-black"
      variant={"destructive"}
    >
      <TrashIcon className="h-4 w-4" />
    </Button>
  </span>
)

export const CollectionDetailModal = ({
  id,
  open,
  onClose,
}: {
  id: string
  open: boolean
  onClose: () => void
}) => {
  const { isAdmin } = useUserStore()
  const { pathname } = useLocation()
  const { data: collection } = useCollection(id)
  // Parse location coordinates
  const coordinates = useMemo(() => {
    if (!collection?.location) return null
    return parseWkbPoint(collection.location)
  }, [collection])

  const [viewState, setViewState] = useState({
    longitude: coordinates?.longitude || 124,
    latitude: coordinates?.latitude || -28,
    zoom: 8,
  })

  const {
    open: openUpdateModal,
    isOpen: isOpenUpdateModal,
    close: closeUpdateModal,
  } = useOpenClose()
  const {
    open: openDeleteModal,
    isOpen: isOpenDeleteModal,
    close: closeDeleteModal,
  } = useOpenClose()

  const { mutateAsync: deleteCollection, isPending: isPendingDelete } =
    useDeleteCollection()
  const { toast } = useToast()

  const handleDelete = useCallback(async () => {
    if (!collection) return
    await deleteCollection(collection.id)
    closeDeleteModal()
    close()
    toast({ description: "Collection deleted successfully." })
  }, [closeDeleteModal, deleteCollection, toast, collection])

  const [modalImage, setModalImage] = useState<number>()

  const { data: trip } = useTripDetail(collection?.trip_id ?? undefined)

  const { photos } = useCollectionPhotos(collection?.id)
  const handleClickPhoto = useCallback(
    (photo: CollectionPhotoSignedUrl) => {
      const index = photos?.findIndex((p) => p.id === photo.id)
      if (index !== undefined) setModalImage(index)
    },
    [setModalImage, photos],
  )

  const { data: audio } = useCollectionAudio(collection?.id)

  const { data: people } = usePeople()
  const creator = people?.find((person) => person.id === collection?.created_by)
  const { data: persons } = usePersons()
  const presentPeople = persons?.filter((person) =>
    collection?.person_ids?.includes(person.id),
  )
  const formattedDuration = formatDuration(collection?.duration)
  const materialTypes = toMaterialTypes(collection?.material_type)

  const { data: collectionContainers } = useCollectionContainers(collection?.id)
  const containerSummary = useMemo(
    () =>
      [...(collectionContainers ?? [])]
        .sort((a, b) => a.container.name.localeCompare(b.container.name))
        .map(({ id, amount, container }) => ({
          id,
          label:
            amount === null ? container.name : `${amount} × ${container.name}`,
        })),
    [collectionContainers],
  )

  if (!collection) return null

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onClose}
        className="max-h-[90vh] overflow-y-auto"
        title={
          <div className="flex justify-between">
            <span>Collection</span>
            <Button
              size={"icon"}
              onClick={onClose}
              title="Close"
              variant={"ghost"}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        }
      >
        <span className="pb-2 text-lg font-bold">{collection.code}</span>
        <div className="space-y-4">
          {/* Species Info */}
          {collection.species_id && (
            <div>
              <div className="text-lead mb-1">Species</div>
              <SpeciesListItem id={collection.species_id} />
            </div>
          )}
          {!collection.species_id && collection.field_name && (
            <div>
              <div className="text-lead mb-1">Species unknown</div>
              <div className="text-sm">Field Name:</div>
              <div>{collection.field_name}</div>
            </div>
          )}
          <Tabs defaultValue="details">
            <TabsList className="bg-secondary-background grid w-full auto-cols-fr grid-flow-col">
              <TabsTrigger value="details">Details</TabsTrigger>
              {coordinates && <TabsTrigger value="map">Map</TabsTrigger>}
              {photos && photos.length > 0 && (
                <TabsTrigger value="photos">Photos</TabsTrigger>
              )}
              {audio && audio.length > 0 && (
                <TabsTrigger value="audio">Audio</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="details">
              <div className="space-y-2">
                <div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-muted-foreground h-min text-left align-middle font-medium">
                          Trip
                        </th>
                        <th className="text-muted-foreground h-min text-left align-middle font-medium">
                          Collected on
                        </th>
                        <th className="text-muted-foreground h-min text-left align-middle font-medium">
                          Recorded at
                        </th>
                        <th className="text-muted-foreground h-min text-left align-middle font-medium">
                          Recorded by
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="h-min align-middle">
                          {trip && (
                            <>
                              {pathname !== `/trips/${collection.trip_id}` &&
                                collection.trip_id && (
                                  <Link
                                    to={`/trips/$id`}
                                    params={{ id: collection.trip_id }}
                                    className="text-foreground underline"
                                  >
                                    {trip.name}
                                  </Link>
                                )}
                              {pathname === `/trips/${collection.trip_id}` && (
                                <Button
                                  className="text-foreground bg-transparent p-0 text-xs underline hover:bg-transparent"
                                  onClick={onClose}
                                >
                                  <span>{trip.name}</span>
                                </Button>
                              )}
                            </>
                          )}
                        </td>
                        <td className="h-min align-middle">
                          {new Date(
                            collection.collected_on,
                          ).toLocaleDateString()}
                        </td>
                        <td className="h-min align-middle">
                          {new Date(collection.created_at).toLocaleString()}
                        </td>
                        <td className="h-min align-middle">
                          {creator?.name || "Unknown Person"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {formattedDuration && (
                  <div>
                    <div className="text-lead mb-1">Duration</div>
                    <div className="text-sm">{formattedDuration}</div>
                  </div>
                )}
                {materialTypes.length > 0 && (
                  <div>
                    <div className="text-lead mb-1">Material Collected</div>
                    <div className="flex flex-wrap gap-2">
                      {materialTypes.map((materialType) => (
                        <Badge key={materialType} variant="secondary">
                          {MATERIAL_TYPE_LABELS[materialType]}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {containerSummary.length > 0 && (
                  <div>
                    <div className="text-lead mb-1">Containers</div>
                    <div className="flex flex-wrap gap-2">
                      {containerSummary.map(({ id, label }) => (
                        <Badge key={id} variant="secondary">
                          {label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {collection.phenology_start !== null && (
                  <div className="space-y-2">
                    <span className="text-muted-foreground h-min text-left align-middle font-medium">
                      Phenology
                    </span>
                    <PhenologyRangeDisplay
                      value={[
                        collection.phenology_start,
                        collection.phenology_peak,
                        collection.phenology_end,
                      ]}
                    />
                  </div>
                )}
                {presentPeople && presentPeople.length > 0 && (
                  <div>
                    <div className="text-lead mb-1">People present</div>
                    <div className="flex flex-wrap gap-2">
                      {presentPeople.map((person) => (
                        <Badge
                          key={person.id}
                          variant={person.is_active ? "default" : "secondary"}
                        >
                          {person.display_name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="flex gap-2">
                    {collection.species_uncertain ? (
                      <Badge variant={"secondary"}>ID uncertain</Badge>
                    ) : (
                      <Badge>ID certain</Badge>
                    )}
                    {collection.specimen_collected && (
                      <Badge>Specimen Collected</Badge>
                    )}
                  </div>
                </div>
                {collection.description && (
                  <div>
                    <div className="text-lead mb-1">Description</div>
                    <div className="bg-secondary-background rounded-sm p-1 text-sm">
                      {collection.description}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="map">
              {coordinates && (
                <div className="h-[350px] w-full">
                  <Map
                    mapLib={mapboxgl as never}
                    mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
                    mapStyle="mapbox://styles/mapbox/satellite-v9"
                    {...viewState}
                    onMove={(evt) => setViewState(evt.viewState)}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Marker
                      longitude={coordinates.longitude}
                      latitude={coordinates.latitude}
                    >
                      <div className="rounded-full bg-white/50 p-2">
                        <ShoppingBag className="text-primary h-5 w-5" />
                      </div>
                    </Marker>
                  </Map>
                </div>
              )}
            </TabsContent>
            <TabsContent value="photos">
              <PhotosTab
                photos={photos ?? []}
                onClickPhoto={handleClickPhoto}
              />
            </TabsContent>
            <TabsContent value="audio">
              <AudioTab audio={audio ?? []} />
            </TabsContent>
          </Tabs>
        </div>
        {isAdmin && (
          <div className="flex justify-end">
            <EditButtons
              openUpdateModal={openUpdateModal}
              openDeleteModal={openDeleteModal}
            />
          </div>
        )}
      </Modal>
      {isOpenUpdateModal && (
        <UpdateCollectionWizardModal
          instance={collection}
          open={isOpenUpdateModal}
          close={closeUpdateModal}
        />
      )}
      {isOpenDeleteModal && (
        <Modal
          open={Boolean(isOpenDeleteModal)}
          title={`Delete Collection`}
          onCancel={closeDeleteModal}
          onSubmit={handleDelete}
          isPending={isPendingDelete}
          allowSubmit={!isPendingDelete}
        >
          This action cannot be undone. This will permanently delete the
          collection.
        </Modal>
      )}
      {
        <Dialog
          open={modalImage !== undefined}
          onOpenChange={(isOpen) => (!isOpen ? setModalImage(undefined) : null)}
        >
          <DialogContent className="lg:min-w-[720px]">
            {/* DialogTitle required for screenreaders */}
            <DialogTitle hidden>Species Image</DialogTitle>
            <Carousel opts={{ startIndex: modalImage }} className="pt-4">
              <CarouselContent>
                {photos?.map((img) => (
                  <CarouselItem key={img.url} className="my-auto h-full p-0">
                    <div className="relative flex h-full w-full flex-col gap-1 text-center">
                      <img
                        src={`${img.signedUrl}`}
                        className="max-h-[564px] w-full object-contain"
                      />
                      <div>
                        {img.caption && (
                          <span>
                            <span className="text-secondary">
                              {img.caption}
                            </span>{" "}
                            ·
                          </span>
                        )}{" "}
                        {img.uploaded_at && (
                          <span className="text-sm">
                            {new Date(img.uploaded_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </DialogContent>
        </Dialog>
      }
    </>
  )
}
